import { useNavigate, useOutletContext } from "react-router-dom";
import { useEffect, useState } from "react";
import { CollectionPageChildProps } from "../CollectionPage";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BookingEngine,
  detectBookingEngine,
  GuessedBookingEngine,
  patchCollection,
} from "@/services/collection";
import { bookingEngines } from "@/services/collection";
import { infoStepperOutletContext } from "../InfoStepper";

export default function HotelBookingEngine({
  collection,
  onRefresh,
}: CollectionPageChildProps) {
  const { setProgress } = useOutletContext<infoStepperOutletContext>();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [guessingBookingEngine, setGuessingBookingEngine] = useState(true);
  const [guessBookingEngineResult, setGuessBookingEngineResult] =
    useState<GuessedBookingEngine>();
  const [guessedBookingEngine, setGuessedBookingEngine] =
    useState<BookingEngine>();

  const [isModified, setIsModified] = useState(false);

  const [changeBookingEngine, setChangeBookingEngine] = useState(false);

  const formSchema = z.object({
    bookingEngine: z.enum(bookingEngines),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bookingEngine: "none",
    },
  });

  useEffect(() => {
    setProgress(40);
  }, []);

  useEffect(() => {
    if (collection?.website) {
      guessBookingEngine(collection.website);
    }
  }, [collection]);

  const guessBookingEngine = async (url: string) => {
    try {
      const res = await detectBookingEngine(url);
      setGuessBookingEngineResult(res);
      if (res.priceProvider) {
        setGuessedBookingEngine(res.priceProvider.type);
        form.reset({
          bookingEngine: res.priceProvider.type,
        });
      } else if (res.guesses) {
        setGuessedBookingEngine(res.guesses[0]);
        form.reset({
          bookingEngine: res.guesses[0],
        });
      }
    } catch {
      console.log("error guessing booking engine");
    }
    setGuessingBookingEngine(false);
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      await patchCollection({
        collectionId: collection?._id,
        bookingEngine: data.bookingEngine,
        priceProviders: guessedBookingEngine,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      await onRefresh();
      navigate(`/collection/${collection?._id}/infos/hotelprice`);
    }
  };

  if (guessingBookingEngine) {
    return (
      <div className="flex flex-col justify-center items-center w-full h-full gap-6">
        <p>{t("onboarding.detecting_booking_engine")}</p>
        <Loader2 className="mr-2 h-12 w-12 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="mt-5 mb-10">
        <p
          className="text-lg text-gray-600"
          dangerouslySetInnerHTML={{
            __html: t("onboarding.bookingEngine_body"),
          }}
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="flex flex-col gap-6">
              <div>
                {guessedBookingEngine ? (
                  <div className="flex gap-4 items-center">
                    {isModified ? (
                      <p>{t("onboarding.user_selected_booking_engine")} </p>
                    ) : (
                      <p>{t("onboarding.detected_booking_engine")} </p>
                    )}
                    <strong className="bg-gray-200 p-2 px-4 rounded-xl">
                      {guessedBookingEngine === "none"
                        ? t("collection.no_booking_engine")
                        : guessedBookingEngine === "other"
                        ? t("collection.other_booking_engine")
                        : guessedBookingEngine}
                    </strong>
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-4 items-center">
                      <p>{t("onboarding.failed_booking_engine_detection")}</p>
                    </div>
                    {changeBookingEngine ? null : (
                      <button
                        className="underline"
                        type="button"
                        onClick={() => setChangeBookingEngine(true)}
                      >
                        {t("onboarding.select_booking_engine_button")}
                      </button>
                    )}
                  </div>
                )}
                {isModified &&
                (guessBookingEngineResult?.priceProvider?.type ||
                  (guessBookingEngineResult?.guesses?.length ?? 0) > 0) ? (
                  <button
                    className="underline"
                    type="button"
                    onClick={() => {
                      if (guessBookingEngineResult?.priceProvider) {
                        setGuessedBookingEngine(
                          guessBookingEngineResult?.priceProvider.type
                        );
                        form.setValue(
                          "bookingEngine",
                          guessBookingEngineResult?.priceProvider.type
                        );
                      } else if (guessBookingEngineResult?.guesses) {
                        setGuessedBookingEngine(
                          guessBookingEngineResult?.guesses[0]
                        );
                        form.setValue(
                          "bookingEngine",
                          guessBookingEngineResult?.guesses[0]
                        );
                      }
                      setIsModified(false);
                    }}
                  >
                    {t("onboarding.detect_booking_engine_button")}
                  </button>
                ) : null}
                {guessedBookingEngine ? (
                  <div className="flex gap-2 items-center">
                    <p>{t("onboarding.wrong_booking_engine_message")}</p>
                    {changeBookingEngine ? null : (
                      <button
                        className="underline"
                        type="button"
                        onClick={() => setChangeBookingEngine(true)}
                      >
                        {t("onboarding.modify_booking_engine_button")}
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
              {changeBookingEngine ? (
                <div className="pl-2">
                  <FormField
                    control={form.control}
                    name="bookingEngine"
                    render={({ field }) => (
                      <FormItem className="board">
                        <FormLabel>
                          {t("collection.booking_engine")} {t("form.required")}
                        </FormLabel>
                        <div className="flex w-1/2 flex-col gap-4 ">
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {bookingEngines.map((be, i) => (
                                <SelectItem key={i} value={be}>
                                  {be === "none"
                                    ? t("collection.no_booking_engine")
                                    : be === "other"
                                    ? t("collection.other_booking_engine")
                                    : be}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex gap-6">
                            <button
                              className="underline"
                              onClick={() => {
                                const newBookingEngine =
                                  form.getValues("bookingEngine");

                                setIsModified(
                                  newBookingEngine !==
                                    guessBookingEngineResult?.priceProvider
                                      ?.type &&
                                    !guessBookingEngineResult?.guesses?.includes(
                                      newBookingEngine
                                    )
                                );
                                setGuessedBookingEngine(newBookingEngine);
                                setChangeBookingEngine(false);
                              }}
                            >
                              {t("onboarding.confirm_booking_engine_button")}
                            </button>
                            <button
                              className="underline"
                              onClick={() => setChangeBookingEngine(false)}
                            >
                              {t("onboarding.abort_booking_engine_button")}
                            </button>
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : null}
            </div>
          </form>
        </Form>
      </div>

      <div className="flex flex-row justify-between mt-6">
        <Button
          className=""
          type="button"
          onClick={() =>
            navigate(`/collection/${collection?._id}/infos/hoteldata`)
          }
        >
          {t("auth.back")}
        </Button>
        {!isLoading ? (
          <Button
            variant="takyon"
            type="submit"
            onClick={form.handleSubmit(onSubmit)}
            disabled={
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              changeBookingEngine
            }
          >
            {t("buttons.save_and_continue")}
          </Button>
        ) : (
          <Button variant="takyon" disabled>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("buttons.loading")}
          </Button>
        )}
      </div>
    </div>
  );
}
